<template>
  <div class="min-h-screen">
    <!-- 顶部导航栏 -->
    <header class="bg-white shadow-sm fixed top-0 left-0 right-0 z-50">
      <div class="px-4 py-3 theme-header">
        <h1 class="text-lg font-semibold text-center text-gray-800 relative">
          GPS定位
          <span class="absolute top-[-10px] right-[-5px] text-xl opacity-30 transform rotate-30">🐾</span>
        </h1>
      </div>
    </header>

    <!-- 主要内容区域 -->
    <main class="pt-16 pb-20">
      <!-- GPS开关控制面板 -->
      <div class="mx-4 mt-4 bg-white rounded-2xl p-4 shadow-sm">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <img class="satelliteIcon" src="https://airpets-image.oss-cn-chengdu.aliyuncs.com/icon/satellite.png"/>
            <!-- <font-awesome-icon icon="satellite" class="text-xl theme-icon" /> -->
            <div>
              <h2 class="font-medium text-gray-800">GPS定位</h2>
              <p class="text-sm text-gray-500">开启后可实时获取位置信息</p>
            </div>
          </div>
          <div 
            :class="['toggle-switch', { active: isGPSEnabled }]" 
            @click="toggleGPS"
          ></div>
        </div>
      </div>

      <!-- 蜂鸣器开关控制面板 -->
      <div class="mx-4 mt-3 bg-white rounded-2xl p-4 shadow-sm">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <img class="bellIcon" src="https://airpets-image.oss-cn-chengdu.aliyuncs.com/icon/bell.png"/>
            <!-- <font-awesome-icon icon="bell" class="text-xl theme-icon" /> -->
            <div>
              <h2 class="font-medium text-gray-800">蜂鸣器提醒</h2>
              <p class="text-sm text-gray-500">开启后在定位变化时发出声音提醒</p>
            </div>
          </div>
          <div
            :class="[
              'toggle-switch',
              { active: isBuzzerEnabled, 'opacity-50 pointer-events-none': !isGPSEnabled }
            ]"
            @click="isGPSEnabled && toggleBuzzer()"
          >
          </div>
        </div>
      </div>

      <!-- 地图容器 -->
      <div class="map-container theme-gradient-bg" :class="{ 'map-disabled': !isGPSEnabled }">
        <div id="container"></div>
      </div>

      <!-- 定位信息卡片 -->
      <div class="mx-4 bg-white rounded-2xl shadow-sm overflow-hidden">
        <div class="p-4">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center space-x-3">
              <div class="relative">
                <div :class="['w-3 h-3 rounded-full', isGPSEnabled ? 'bg-green-400' : 'bg-gray-400']"></div>
                <div :class="['absolute inset-0 w-3 h-3 rounded-full animate-ping opacity-75', isGPSEnabled ? 'bg-green-400' : 'bg-gray-400']"></div>
              </div>
              <div class="flex flex-col">
                <span class="text-base font-medium text-gray-700">{{ isGPSEnabled ? 'GPS已开启' : 'GPS已关闭' }}</span>
                <span class="text-xs text-gray-500">{{ isGPSEnabled ? '正在获取位置信息' : '开启GPS以获取位置信息' }}</span>
              </div>
            </div>
            <!-- <button 
              v-show="isGPSEnabled"
              class="px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-full transition-colors flex items-center space-x-1"
              :disabled="refreshing"
              @click="updateLocation(true)"
            >
              <font-awesome-icon :icon="refreshing ? 'spinner' : 'location-arrow'" :spin="refreshing" />
              <span class="ml-1">{{ refreshing ? '获取中...' : '获取位置' }}</span>
            </button> -->
          </div>
          <div v-show="isGPSEnabled" class="space-y-3 text-sm text-gray-600">
            <!-- 速度信息 -->
            <div class="bg-blue-50 p-3 rounded-xl">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-2">
                  <img style="width: 15px;height: 15px;" src="https://airpets-image.oss-cn-chengdu.aliyuncs.com/icon/tachometer-alt.png"/>
                  <!-- <font-awesome-icon icon="tachometer-alt" class="text-blue-500" /> -->
                  <span class="text-gray-500">实时速度</span>
                </div>
                <span :class="[
                  'text-xs px-2 py-1 rounded-full',
                  signalStrength === '信号良好' ? 'bg-green-100 text-green-600' :
                  signalStrength === '信号一般' ? 'bg-yellow-100 text-yellow-600' :
                  'bg-red-100 text-red-600'
                ]">{{ realTimeMotion.sat }}</span>
              </div>
              <div class="mt-1 pl-6">
                <span class="text-2xl font-semibold text-gray-700">{{ realTimeMotion.speed }}</span>
                <span class="text-sm text-gray-500 ml-1">m/s</span>
              </div>
              <div class="mt-1 pl-6">
                <span class="text-xs text-gray-500">室内环境可能无法获取准确的速度信息</span>
              </div>
            </div>

            <!-- 更新时间 -->
            <div class="text-center text-xs text-gray-500 pt-2">
              最后更新：<span>{{ realTimeMotion.locationTime }}</span>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- 底部导航栏 -->
    <nav class="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 shadow-lg">
      <div class="grid grid-cols-5 py-2 theme-header">
        <router-link to="/home" exact active-class="nav-active" class="routerLink">
          <!-- <font-awesome-icon icon="home" class="text-xl" /> -->
          <!-- <img style="width: 22px;height: 20px;" src="https://airpets-image.oss-cn-chengdu.aliyuncs.com/icon/selHome.png"/> -->
          <img style="width: 22px;height: 20px;" src="https://airpets-image.oss-cn-chengdu.aliyuncs.com/icon/home.png"/>
          <p class="text-xs mt-1">首页</p>
        </router-link>
        <router-link to="/location" exact active-class="nav-active" class="routerLink">
          <!-- <font-awesome-icon icon="map-marker-alt" class="text-xl" /> -->
          <img style="width: 22px;height: 22px;" src="https://airpets-image.oss-cn-chengdu.aliyuncs.com/icon/SelPosition.png"/>
          <!-- <img style="width: 22px;height: 22px;" src="https://airpets-image.oss-cn-chengdu.aliyuncs.com/icon/position.png"/> -->
          <p class="text-xs mt-1">定位</p>
        </router-link>
        <router-link to="/ai-diagnosis" exact active-class="nav-active" class="routerLink">
          <!-- <font-awesome-icon icon="robot" class="text-xl" /> -->
          <img style="width: 20px;height: 20px;" src="https://airpets-image.oss-cn-chengdu.aliyuncs.com/icon/robotAi.png"/>
          <!-- <img style="width: 20px;height: 20px;" src="https://airpets-image.oss-cn-chengdu.aliyuncs.com/icon/selRobotAi.png"/> -->
          <p class="text-xs mt-1">AI问诊</p>
        </router-link>
        <router-link to="/device-list" exact active-class="nav-active" class="routerLink">
          <!-- <font-awesome-icon icon="microchip" class="text-xl" /> -->
          <img style="width: 20px;height: 20px;" src="https://airpets-image.oss-cn-chengdu.aliyuncs.com/icon/microchip.png"/>
          <!-- <img style="width: 20px;height: 20px;" src="https://airpets-image.oss-cn-chengdu.aliyuncs.com/icon/selMicrochip.png"/> -->
          <p class="text-xs mt-1">设备</p>
        </router-link>
        <router-link to="/profile" exact active-class="nav-active" class="routerLink">
          <img style="width: 20px;height: 20px;" src="https://airpets-image.oss-cn-chengdu.aliyuncs.com/icon/user.png"/>
          <!-- <img style="width: 20px;height: 20px;" src="https://airpets-image.oss-cn-chengdu.aliyuncs.com/icon/selUser.png"/> -->
          <!-- <font-awesome-icon icon="user" class="text-xl" /> -->
          <p class="text-xs mt-1">我的</p>
        </router-link>
      </div>
    </nav>
  </div>
</template>

<script>
import AMapLoader from "@amap/amap-jsapi-loader";
import { RPC } from "@/api/rpc";
/**
 * GPS定位页面组件
 * 显示实时位置信息
 */
export default {
  name: 'Location',
  data() {
    return {
      map: null,
      locationMarker: null,
      circle: null,
      updateInterval: null,
      isGPSEnabled: false,
      isBuzzerEnabled: false,
      refreshing: false,
      currentSpeed: '0.0',
      signalStrength: '信号良好',
      updateTime: '--:--',
      L: null, // 存储Leaflet库的引用
      AMap: null,
      info:{},
      endLng: '',
      endLat: '',
      currentLng: '',
      currentLat: '',
      machineId: null,
      machineInfoObj: {},
      realTimeMotion: {},
      locationTime: '',
      timer: null,
    }
  },
  async mounted() {
    clearTimeout(this.timer)
    this.machineId = sessionStorage.getItem('deviceCode')?sessionStorage.getItem('deviceCode') : null
    window._AMapSecurityConfig = { securityJsCode: 'a1d997561aa71dc76a4c8524f08723dc' }
    this.loadLeaflet();
    RPC.machineInfo({machineId:this.machineId}).then(res=>{
      this.machineInfoObj = res.data
      if (res.data.isGps == 1) {
        this.trackInfo()
        this.updateLocation(true)
      }
      this.isGPSEnabled = res.data.isGps == 1?true:false
    })
  },
  beforeDestroy() {
    this.stopLocationUpdates();
    clearTimeout(this.timer)
  },
  methods: {
    async initAMap() {
      if (!this.L) return;
      
      const AMap = await AMapLoader.load({
        key: '2d5e8d080553128da903df858cb0b417',
        version: '2.0',
        plugins: ['AMap.Walking','AMap.Geolocation','AMap.Driving']  // 声明步行路线插件
      });
      this.AMap = AMap;
      this.machineInfo()
    },
    machineInfo() {
      RPC.machineLocation({machineId:this.machineId}).then(async res=>{
        if (res.data) {
          this.locationTime = res.data.locationTime
          this.info = res.data
          this.cover()
        } else {
          this.getLocation()
        }
      })
    },
    cover() {
      var gps = [this.info.machineLongitude, this.info.machineLatitude];
      this.AMap.convertFrom(gps, 'gps',  (status, result)=> {
        if (result.info === 'ok') {
          var lnglats = result.locations; // Array.<LngLat>
            this.endLng = lnglats[0].lng
            this.endLat = lnglats[0].lat
            this.getLocation()
        }
      });
    },
    getLocation() {//获取当前定位
      const geolocation = new this.AMap.Geolocation({
        enableHighAccuracy: true, // 是否使用高精度定位，默认:true
        timeout: 10000,           // 超过10秒后停止定位，默认：5s
        buttonPosition: 'RB',     // 定位按钮的停靠位置
        buttonOffset: new this.AMap.Pixel(10, 20), // 定位按钮距离对应角落的距离
        zoomToAccuracy: true
      })
      geolocation.getCurrentPosition((status, result) => {
        if(status=='complete'){
          console.log('定位成功',result)
          this.currentLng = result.position.lng
          this.currentLat = result.position.lat
          this.map = new this.AMap.Map('container', { zoom: 17 ,center: [this.currentLng,this.currentLat ],zoomToAccuracy: true, });
          
          if (this.marker) {
            this.marker.setMap(null);
          }
          this.marker = new this.AMap.Marker({
            position: [this.currentLng, this.currentLat],
            map: this.map,
            title: '当前位置'
          });
          this.initWalkingRoute();
        }
      })
    },
    initWalkingRoute() {
      var walking = new this.AMap.Walking({
        map: this.map,
      })
      walking.search([this.currentLng,this.currentLat ], [this.endLng, this.endLat],(status, result)=> {
        if (status === 'complete') {
            console.log('绘制步行路线完成')
        } else {
          console.log('步行路线数据查询失败' + result)
          this.goDriving()
        } 
      });
    },
    goDriving() {
      var Driving = new this.AMap.Driving({
        map: this.map,
      })
      Driving.search([this.currentLng,this.currentLat ], [this.endLng, this.endLat],(status, result)=> {
        if (status === 'complete') {
            console.log('绘制驾车路线完成')
        } else {
          console.log('获取驾车数据失败' + result)
        } 
      });
    },
    /**
     * 加载Leaflet库
     */
    loadLeaflet() {
      if (window.L) {
        // 如果Leaflet已经加载
        this.L = window.L;
        this.initAMap();
      } else {
        // 如果Leaflet未加载，动态加载它
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.7.1/dist/leaflet.js';
        script.onload = () => {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = 'https://unpkg.com/leaflet@1.7.1/dist/leaflet.css';
          document.head.appendChild(link);
          
          // 保存引用并初始化地图
          this.L = window.L;
          this.initAMap();
        };
        document.head.appendChild(script);
      }
    },
    
    /**
     * 初始化地图
     */
    initMap() {
      if (!this.L) return;
    },
    
    /**
     * 切换GPS状态
     */
    toggleGPS() {
      this.isGPSEnabled = !this.isGPSEnabled;
      //1 true开启 2 false关闭
      if (this.isGPSEnabled) {
        this.updateLocation(true)
        // this.startLocationUpdates();
      } else {
        this.isBuzzerEnabled = false;
        this.isBuzzerEnabled = true
        this.toggleBuzzer()
        clearInterval(this.timer);
      }
      let data = {
        "gpsSwitch": this.isGPSEnabled?1:2,
	      "machineId": this.machineId
      }
      RPC.gpsSwitch(data).then(res=>{
        this.$toast.success('操作成功');
        if (this.isGPSEnabled) {
          this.trackInfo()
        }
      })
       // 关闭蜂鸣器
      
    },
    trackInfo() {
      RPC.machineLocation({machineId:this.machineId}).then(async res=>{
        if (res.data) {
          this.realTimeMotion = res.data
          this.refreshing = false
        }
      })
    },
    /**
     * 开始位置更新
     */
    startLocationUpdates() {
      // 立即更新一次
      this.updateLocation(false);
      
      // 设置定时更新
      this.updateInterval = setInterval(() => this.updateLocation(false), 5000);
    },
    
    /**
     * 停止位置更新
     */
    stopLocationUpdates() {
      if (this.updateInterval) {
        clearInterval(this.updateInterval);
        this.updateInterval = null;
      }
    },
    
    /**
     * 切换蜂鸣器状态
     */
    toggleBuzzer() {
      this.isBuzzerEnabled = !this.isBuzzerEnabled;
      // 提示用户蜂鸣器状态已更改
      RPC.buzzerSwitch({
        "buzzerSwitch": this.isBuzzerEnabled?1:2,
	      "machineId": this.machineId
      }).then(res=>{
        if (this.isBuzzerEnabled) {
          this.$toast.success('蜂鸣器提醒已开启');
        } else {
          this.$toast.success('蜂鸣器提醒已关闭');
        }
      } )
    },
    gpsInfo() {
      RPC.machineLocation({machineId:this.machineId}).then(async res=>{
        if (res.data) {
          if (this.locationTime && this.locationTime != res.data.locationTime) {
            clearTimeout(this.timer)
            this.machineInfo()
          } else {
            this.timer = setTimeout(() => {
              this.gpsInfo()
            }, 5000)
          }
        }
      })
    },
    /**
     * 更新位置信息
     * @param {boolean} isManualRefresh - 是否为手动刷新
     */
    updateLocation(isManualRefresh) {
      RPC.petsGetLocation({machineId:this.machineId}).then(res=>{
        this.gpsInfo()
      })
      
      // 如果是手动刷新，显示加载状态
      if (isManualRefresh) {
        this.refreshing = true;
      }
    }
  }
}
</script>

<style scoped>
:root {
  --primary-color: #3B82F6;
  --secondary-color: #93C5FD;
  --accent-color: #2563EB;
  --background-color: #F3F4F6;
}

body {
  background-color: var(--background-color);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

#container {
  width: 100%;
  height: 100%;
  border-radius: 1rem;
  transition: filter 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.map-container {
  height: calc(100vh - 16rem);
  padding: 1rem;
  border-radius: 1rem;
  margin: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.map-disabled {
  filter: grayscale(100%) brightness(0.8);
  pointer-events: none;
}

.toggle-switch {
  width: 3.5rem;
  height: 2rem;
  background-color: #D1D5DB;
  border-radius: 9999px;
  position: relative;
  transition: background-color 0.3s;
  cursor: pointer;
}

.toggle-switch::after {
  content: '';
  position: absolute;
  top: 0.25rem;
  left: 0.25rem;
  width: 1.5rem;
  height: 1.5rem;
  background-color: white;
  border-radius: 50%;
  transition: transform 0.3s, background-color 0.3s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.toggle-switch.active {
  background-color: var(--primary-color);
}

.toggle-switch.active::after {
  transform: translateX(1.5rem);
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.pulse {
  animation: pulse 2s infinite;
}

.rotate-30 {
  transform: rotate(30deg);
}

.location-marker-container {
  background: transparent;
  border: none;
}

.location-marker {
  font-size: 2rem;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}

.nav-active {
  color: var(--accent-color);
}

.nav-active p {
  color: #4B5563; /* text-gray-600 */
}

.nav-active .svg-inline--fa {
  color: #2563EB; /* text-blue-600 */
}

.routerLink {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
}
.satelliteIcon,.bellIcon {
  width: 20px;
  height: 20px;
}
</style> 